import styled, { css } from 'styled-components';

import VdpBox from '../../../_shared/components/VdpBox';
import Box from '../../../_shared/components/Box';
import Grid from '../../../_shared/components/Grid';
import Icon from '../../../_shared/components/Icon';
import Detail from '../../../_shared/components/Detail';
import Badge from '../../../_shared/components/Badge';
import Label from '../../../_shared/components/Label';
import applyResponsiveVdpStyles from '../../../_shared/helpers/applyResponsiveVdpStyles';
import isOmpPlatform from '../../../_shared/utils/isOmpPlatform';

const isOmp = isOmpPlatform();

/**
 * Variant Definitions
 */
const StyledConditionsDisclosuresVariants = {
  mini: css`
    padding: 34px 18px;
  `,
  expanded: css`
    flex-wrap: wrap;
  `
};

const StyledBoxVariants = {
  mini: css`
    margin-bottom: 10px;
  `,
  expanded: css`
    margin-bottom: 30px;
  `
};

const StyledConditionGroupingsVariants = {
  mini: css`
    padding: 0 2px;
    grid-template-columns: 1fr;
    grid-row-gap: inherit;
  `,
  expanded: css`
    padding: 0 40px 36px 0;
    grid-row-gap: 48px;
    grid-template-columns: 1fr 1fr 1fr 1fr;
  `
};

const StyledConditionTitleVariants = {
  mini: css`
    flex-direction: row;
    flex-grow: 1;
    width: 100%;
    align-items: center;
    margin-bottom: 10px;
  `,
  expanded: css`
    flex-direction: column;
  `
};

const StyledTitleVariants = {
  mini: css`
    margin-left: 12px;
    padding: 0;
  `,
  expanded: css`
    margin-left: 2px;
    padding-top: 6px;
    padding-bottom: 10px; // TODO: Remove once See checklist is enabled
  `
};

const StyledConditionItemsVariants = {
  mini: css`
    padding-top: 18px;
    padding-bottom: 10px;
    &:last-child {
      padding-bottom: 0;
    }
  `
};

const StyledConditionIconVariants = {
  mini: css`
    width: 24px;
    height: 24px;
    background-color: ${({ theme }) => theme.COLORS.BASE};
    border-radius: 9px;
    padding: 0;
  `,
  expanded: css`
    width: 48px;
    height: 48px;
    background-color: ${({ theme }) => theme.COLORS.NEUTRAL_5};
    padding: 5px;
  `
};

const StyledChecklistVariants = {
  mini: css`
    margin-left: auto;
  `
};

const StyledDividerVariants = {
  mini: css`
    display: none;
  `,
  expanded: css`
    display: block;
  `
};

const StyledSectionDividerVariants = {
  mini: css`
    display: block;
  `,
  expanded: css`
    display: none;
  `
};

const StyledNotesBoxVariants = {
  mini: css`
    padding-top: 0;
    margin-bottom: 18px;
    align-items: flex-start;
  `,
  expanded: css`
    margin-bottom: 12px;
    align-items: center;
  `
};

const StyledNotesIconVariants = {
  mini: css`
    width: 24px;
    height: 24px;
    padding: 0;
    margin-top: 4px;
    margin-left: 5px;
  `,
  expanded: css`
    width: 48px;
    height: 48px;
    background-color: ${({ theme }) => theme.COLORS.NEUTRAL_5};
    border-radius: 9px;
    padding: 10px;
  `
};

const StyledNotesVariant = {
  true: css`
    color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
  `,
  mini: css`
    margin-bottom: 12px;
    padding: 12px;
  `,
  expanded: css`
    padding: 24px;
  `
};

const StyledDisclosuresVariants = {
  mini: css`
    margin-left: 40px;
    margin-bottom: 12px;
    margin-top: 8px;
  `
};

const StyledNothingReportedVariants = {
  mini: css`
    margin-left: auto;
    padding-bottom: 0;
  `,
  expanded: css`
    margin-left: 2px;
  `
};

const StyledMediaVariants = {
  mini: css`
    &:not(:only-child):not(:last-child) {
      padding-bottom: 6px;
    }
  `,
  expanded: css`
    &:not(:only-child):not(:last-child) {
      padding-bottom: 12px;
    }
  `
};

/**
 * Styled Components
 */
const StyledConditionsDisclosures = styled(VdpBox)`
  display: flex;
  flex-direction: column;
  width: 100%;
  scroll-margin-top: 175px;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledConditionsDisclosuresVariants.mini,
      expanded: StyledConditionsDisclosuresVariants.expanded
    })}
`;

export const StyledBox = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  background-color: ${({ theme }) => theme.COLORS.BASE};
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledBoxVariants.mini,
      expanded: StyledBoxVariants.expanded
    })}
`;

export const StyledConditionHeading = styled.h2`
  font-family: ${({ theme }) => theme.FONT.REGULAR};
  font-size: ${({ theme }) => theme.FONT_SIZES.LARGE};
  color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
`;

export const StyledConditionGroupings = styled(Grid)`
  grid-column-gap: 48px;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledConditionGroupingsVariants.mini,
      expanded: StyledConditionGroupingsVariants.expanded
    })}
`;

export const StyledConditionTitle = styled(Box)`
  display: flex;
  flex-wrap: wrap;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledConditionTitleVariants.mini,
      expanded: StyledConditionTitleVariants.expanded
    })}
`;

export const StyledTitle = styled(Label.Bold)`
  flex-direction: row;
  font-size: ${({ theme }) => theme.FONT_SIZES.MEDIUM};
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledTitleVariants.mini,
      expanded: StyledTitleVariants.expanded
    })}
`;

export const StyledSubTitle = styled.span`
  font-family: ${({ theme }) => theme.FONT.REGULAR};
  font-size: ${({ theme }) => theme.FONT_SIZES.MEDIUM};
  line-height: ${({ theme }) => theme.FONT_SIZES.MEDIUM};
  margin-left: 5px;
`;

export const StyledConditionItems = styled(Box)`
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  padding-left: 0;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledConditionItemsVariants.mini
    })}
`;

export const StyledConditionIcon = styled(Icon).attrs(props => ({
  icon: props.icon
}))`
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledConditionIconVariants.mini,
      expanded: StyledConditionIconVariants.expanded
    })}
`;

export const StyledChecklistIcon = styled(Icon).attrs(props => ({
  icon: props.icon
}))`
  width: 10px;
  height: 10px;
  margin-left: 8.5px;
`;

export const StyledChecklist = styled.button`
  padding-top: 6px;
  padding-left: 2px;
  text-decoration-line: underline;
  padding-bottom: 24px;
  font-family: ${({ theme }) => theme.FONT.REGULAR};
  font-size: ${({ theme }) => theme.FONT_SIZES.SMALL};
  color: ${({ theme }) => theme.COLORS.NEUTRAL_30};
  cursor: pointer;
  text-align: left;
  background-color: ${({ theme }) => theme.COLORS.BASE};
  border: none;
  text-decoration-color: ${({ theme }) => theme.COLORS.NEUTRAL_10};
  display: none; // TODO: Remove once See checklist is enabled

  &:hover {
    color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
    border: none;
  }

  &:focus {
    outline: none;
    box-shadow: none;
  }

  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledChecklistVariants.mini
    })}
`;

export const StyledDivider = styled.div`
  border-top: 3px solid
    ${({ theme }) =>
      isOmp ? theme.COLORS.NEUTRAL_110 : theme.COLORS.NEUTRAL_10};
  width: 100%;
  padding-bottom: 36px;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledDividerVariants.mini,
      expanded: StyledDividerVariants.expanded
    })}
`;

export const StyledSectionDivider = styled.div`
  border-top: 1px solid
    ${({ theme }) =>
      isOmp ? theme.COLORS.NEUTRAL_110 : theme.COLORS.NEUTRAL_10};
  width: 100%;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledSectionDividerVariants.mini,
      expanded: StyledSectionDividerVariants.expanded
    })}
`;

export const StyledNotesBox = styled(Box)`
  flex-direction: row;
  flex-grow: 1;
  width: 100%;
  justify-content: flex-start;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledNotesBoxVariants.mini,
      expanded: StyledNotesBoxVariants.expanded
    })}
`;

export const StyledNotesDisclaimer = styled(Detail.Value)`
  font-size: ${({ theme }) => theme.FONT_SIZES.SMALL};
  margin-top: 5px;
`;

export const StyledNotesIcon = styled(Icon).attrs(props => ({
  icon: props.icon
}))`
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledNotesIconVariants.mini,
      expanded: StyledNotesIconVariants.expanded
    })}
`;

export const NotesHeading = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: 12px;
`;

export const StyledNotesTitle = styled(Label.Bold)`
  font-size: ${({ theme }) => theme.FONT_SIZES.MEDIUM};
`;

export const StyledNotes = styled(Detail.Label)`
  display: block;
  background: ${({ theme }) => theme.COLORS.NEUTRAL_5};
  border-radius: 3px;
  text-align: left;
  white-space: pre-wrap;
  ${props => StyledNotesVariant[props.isNotesAvailable]};
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledNotesVariant.mini,
      expanded: StyledNotesVariant.expanded
    })}
`;

export const StyledBadge = styled(Badge)`
  background-color: ${({ theme }) => theme.COLORS.ORANGE};
  border-radius: 18px;
  margin-left: 12px;
  font-size: ${({ theme }) => theme.FONT_SIZES.BASE};
  height: ${isOmp ? '26px' : '24px'};
  text-align: center;
  padding: 0px 6px;
`;

export const StyledDisclosureCountBadge = styled(Badge)`
  border: 2px solid ${({ theme }) => theme.COLORS.ORANGE};
  color: ${({ theme }) => theme.COLORS.ORANGE};
  min-width: 36px;
  border-radius: 18px;
  text-align: center;
  margin: 0 12px;
`;

export const StyledThirdPartyInspectedContent = styled.span`
  width: 180px;
  font-family: ${({ theme }) => theme.FONT.SEMI_BOLD};
  font-size: ${({ theme }) => theme.FONT_SIZES.SMALL};
  line-height: ${({ theme }) => theme.FONT_SIZES.SMALL};
  text-align: right;
  text-decoration-line: underline;
  margin-left: auto;
  color: #000000;
}`;

export const StyledThirdPartyInspected = styled(Icon).attrs(props => ({
  icon: props.icon
}))`
  width: 32.91px;
  height: 36px;
  margin-left: 12px;
`;

export const StyledDisclosures = styled.span`
  font-family: ${({ theme }) => theme.FONT.REGULAR};
  font-size: ${({ theme }) => theme.FONT_SIZES.BASE};
  line-height: ${({ theme }) => theme.FONT_SIZES.BASE};
  flex-direction: column;
  background-color: ${({ theme }) => theme.COLORS.BASE};
  margin-right: 12px;
  position: relative;
  &:before {
    position: absolute;
    left: -3px;
    content: ' ';
    border-radius: 3px;
    background: ${({ theme }) => theme.COLORS.ORANGE};
    width: 4px;
    height: 100%;
  }
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledDisclosuresVariants.mini
    })}
`;

export const StyledDisclosuresLabel = styled(Detail.Value)`
  padding-bottom: 12px;
  padding-left: 12px;

  &:only-child {
    padding-bottom: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }

  &:not(:only-child):not(:last-child) {
    padding-bottom: 12px;
  }
`;

export const StyledNothingReported = styled(Detail.Label)`
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledNothingReportedVariants.mini,
      expanded: StyledNothingReportedVariants.expanded
    })}
`;

export const StyledMedia = styled(Box)`
  display: flex;
  flex-direction: row;
  padding-bottom: 12px;
  padding-left: 12px;
  align-items: center;

  &:only-child {
    padding-bottom: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }

  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledMediaVariants.mini,
      expanded: StyledMediaVariants.expanded
    })}
`;

export const StyledMediaLink = styled.a`
  display: inherit;
  border: none;
  outline: none;
  cursor: pointer;
  font-family: ${({ theme }) => theme.FONT.REGULAR};
  font-size: 16px;
  letter-spacing: normal;
  background-color: ${({ theme }) => theme.COLORS.BASE};
  text-transform: inherit;
  text-decoration: underline;
  color: ${({ theme }) => theme.COLORS.TERTIARY};
  text-decoration-color: ${({ theme }) => theme.COLORS.NEUTRAL_20};
  padding-left: 12px;
  &:hover {
    color: ${({ theme }) => theme.COLORS.DARK_BLUE};
  }
`;

export const StyledGalleryIcon = styled(Icon).attrs(props => ({
  icon: props.icon
}))`
  width: 13px;
  height: 11px;
`;

export default StyledConditionsDisclosures;
